'use client';
import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import { Calculator, CheckCircle, AlertTriangle, Edit3, Search, ChevronDown, ChevronUp, Eye, X, Save, Info, CheckSquare, Square, Layers, Home, Building2, Warehouse, MapPin, TreePine, Globe, Flag, FileText, Plus, ToggleLeft, ToggleRight, FlaskConical, BookOpen, Send, Zap,  } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

type PropertyClass =
  | 'Residential' | 'Commercial' | 'Industrial' | 'Vacant Land' |'Farmland' | 'Public-Land' | 'Mixed Use' | 'Institutional' | 'Special Economic Zone';

type LocationZone =
  | 'Zone A – Central Monrovia' | 'Zone B – Greater Monrovia' |'Zone C – Secondary Cities' | 'Zone D – Rural Towns' | 'Zone E – Remote/Rural';

type FormulaStatus = 'active' | 'draft' | 'archived';
type ParcelQueueStatus = 'queued' | 'formula_assigned' | 'calculated' | 'previewed' | 'approved' | 'forwarded';
type ActiveTab = 'formulas' | 'queue' | 'preview' | 'batch' | 'audit';

interface ValuationFormula {
  id: string;
  name: string;
  propertyClass: PropertyClass;
  locationZone: LocationZone;
  baseRatePerSqm: number;
  locationMultiplier: number;
  useMultiplier: number;
  depreciationRate: number;
  minTax: number;
  maxTax: number;
  notes: string;
  status: FormulaStatus;
  lastUpdated: string;
  updatedBy: string;
  effectiveDate: string;
}

interface QueuedParcel {
  id: string;
  parcelId: string;
  owner: string;
  taxpayerId: string;
  county: string;
  district: string;
  propertyClass: PropertyClass;
  locationZone: LocationZone;
  areaSqm: number;
  buildingAreaSqm: number;
  constructionGrade: 'A' | 'B' | 'C' | 'D';
  tenureType: string;
  assessedLandValue: number;
  assessedImprovementValue: number;
  totalAssessedValue: number;
  status: ParcelQueueStatus;
  assignedFormulaId: string | null;
  calculatedTax: number | null;
  overrideTax: number | null;
  overrideReason: string;
  approvedBy: string;
  approvedDate: string;
  forwardedDate: string;
  s3ApprovedDate: string;
  notes: string;
  flags: string[];
  selected: boolean;
}

interface AuditEntry {
  id: string;
  timestamp: string;
  action: string;
  officer: string;
  target: string;
  details: string;
  oldValue: string;
  newValue: string;
  category: 'formula' | 'assignment' | 'calculation' | 'approval' | 'forward' | 'override' | 'system';
  status: 'info' | 'success' | 'warning' | 'error';
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PROPERTY_CLASSES: PropertyClass[] = [
  'Residential', 'Commercial', 'Industrial', 'Vacant Land',
  'Farmland', 'Public-Land', 'Mixed Use', 'Institutional', 'Special Economic Zone',
];
const LOCATION_ZONES: LocationZone[] = [
  'Zone A – Central Monrovia', 'Zone B – Greater Monrovia',
  'Zone C – Secondary Cities', 'Zone D – Rural Towns', 'Zone E – Remote/Rural',
];

const FORMULA_STATUS_CFG: Record<FormulaStatus, { label: string; color: string; bg: string }> = {
  active:   { label: 'Active',    color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  draft:    { label: 'Draft',     color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  archived: { label: 'Archived',  color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
};

const QUEUE_STATUS_CFG: Record<ParcelQueueStatus, { label: string; color: string; bg: string }> = {
  queued:           { label: 'Queued',           color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
  formula_assigned: { label: 'Formula Assigned', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  calculated:       { label: 'Calculated',       color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  previewed:        { label: 'Previewed',        color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  approved:         { label: 'Approved',         color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  forwarded:        { label: 'Forwarded → S5',   color: '#6ee7b7', bg: 'rgba(110,231,183,0.10)' },
};

const PROP_ICON: Record<PropertyClass, React.ReactNode> = {
  Residential:          <Home size={12} />,
  Commercial:           <Building2 size={12} />,
  Industrial:           <Warehouse size={12} />,
  'Vacant Land':        <MapPin size={12} />,
  Farmland:             <TreePine size={12} />,
  'Public-Land':        <Layers size={12} />,
  'Mixed Use':          <Layers size={12} />,
  Institutional:        <Layers size={12} />,
  'Special Economic Zone': <Globe size={12} />,
};

const AUDIT_CAT_CFG: Record<AuditEntry['category'], { color: string; bg: string }> = {
  formula:     { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  assignment:  { color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  calculation: { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  approval:    { color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  forward:     { color: '#6ee7b7', bg: 'rgba(110,231,183,0.12)' },
  override:    { color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  system:      { color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
};

const fmt = (n: number) => `$${n.toLocaleString()}`;
const fmtK = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : fmt(n);

// ─── Formula Calculation ──────────────────────────────────────────────────────

function calcTax(f: ValuationFormula, area: number, buildingArea: number, grade: string): number {
  const gradeM = grade === 'A' ? 1.2 : grade === 'B' ? 1.0 : grade === 'C' ? 0.85 : 0.70;
  const gross = area * f.baseRatePerSqm * f.locationMultiplier * f.useMultiplier * gradeM;
  const dep = gross * (1 - f.depreciationRate / 100);
  return Math.min(Math.max(Math.round(dep), f.minTax), f.maxTax);
}

function matchFormula(formulas: ValuationFormula[], cls: PropertyClass, zone: LocationZone): ValuationFormula | null {
  return formulas.find(f => f.status === 'active' && f.propertyClass === cls && f.locationZone === zone)
    || formulas.find(f => f.status === 'active' && f.propertyClass === cls)
    || null;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const INITIAL_FORMULAS: ValuationFormula[] = [
  { id: 'VF-001', name: 'Central Monrovia Commercial', propertyClass: 'Commercial', locationZone: 'Zone A – Central Monrovia', baseRatePerSqm: 4.80, locationMultiplier: 1.85, useMultiplier: 1.40, depreciationRate: 2.0, minTax: 1200, maxTax: 85000, notes: 'Applies to all commercial properties in CBD. Reviewed annually per LRA Circular 2025-04.', status: 'active', lastUpdated: '2026-06-15', updatedBy: 'J. Jallah', effectiveDate: '2026-01-01' },
  { id: 'VF-002', name: 'Greater Monrovia Residential', propertyClass: 'Residential', locationZone: 'Zone B – Greater Monrovia', baseRatePerSqm: 1.20, locationMultiplier: 1.30, useMultiplier: 0.85, depreciationRate: 3.5, minTax: 150, maxTax: 12000, notes: 'Standard residential owner-occupied rate. 15% relief applied for primary residence.', status: 'active', lastUpdated: '2026-05-20', updatedBy: 'M. Kollie', effectiveDate: '2026-01-01' },
  { id: 'VF-003', name: 'Industrial Margibi Corridor', propertyClass: 'Industrial', locationZone: 'Zone C – Secondary Cities', baseRatePerSqm: 2.60, locationMultiplier: 1.10, useMultiplier: 1.55, depreciationRate: 4.0, minTax: 3500, maxTax: 200000, notes: 'Covers SEZ and industrial park properties along the Monrovia–Ganta corridor.', status: 'active', lastUpdated: '2026-04-10', updatedBy: 'J. Jallah', effectiveDate: '2026-01-01' },
  { id: 'VF-004', name: 'Rural Farmland Standard', propertyClass: 'Farmland', locationZone: 'Zone E – Remote/Rural', baseRatePerSqm: 0.08, locationMultiplier: 0.60, useMultiplier: 0.70, depreciationRate: 1.0, minTax: 50, maxTax: 8000, notes: 'Concessional rate for subsistence and commercial farming. Exempt if < 2 ha.', status: 'active', lastUpdated: '2026-03-01', updatedBy: 'T. Freeman', effectiveDate: '2026-01-01' },
  { id: 'VF-005', name: 'Vacant Land Urban Penalty', propertyClass: 'Vacant Land', locationZone: 'Zone A – Central Monrovia', baseRatePerSqm: 3.20, locationMultiplier: 2.00, useMultiplier: 1.80, depreciationRate: 0.0, minTax: 800, maxTax: 45000, notes: 'Penalty surcharge for idle urban land. Encourages development. IMF-aligned anti-speculation measure.', status: 'active', lastUpdated: '2026-06-01', updatedBy: 'J. Jallah', effectiveDate: '2026-01-01' },
  { id: 'VF-006', name: 'Secondary City Residential', propertyClass: 'Residential', locationZone: 'Zone C – Secondary Cities', baseRatePerSqm: 0.85, locationMultiplier: 0.95, useMultiplier: 0.80, depreciationRate: 4.0, minTax: 80, maxTax: 6000, notes: 'Residential properties in Gbarnga, Buchanan, Sanniquellie.', status: 'active', lastUpdated: '2026-04-15', updatedBy: 'M. Kollie', effectiveDate: '2026-01-01' },
  { id: 'VF-007', name: 'Mixed Use Central', propertyClass: 'Mixed Use', locationZone: 'Zone B – Greater Monrovia', baseRatePerSqm: 2.10, locationMultiplier: 1.45, useMultiplier: 1.20, depreciationRate: 2.5, minTax: 500, maxTax: 35000, notes: 'Mixed commercial/residential properties in Greater Monrovia.', status: 'active', lastUpdated: '2026-05-10', updatedBy: 'J. Jallah', effectiveDate: '2026-01-01' },
  { id: 'VF-008', name: 'SEZ Industrial Rate', propertyClass: 'Special Economic Zone', locationZone: 'Zone C – Secondary Cities', baseRatePerSqm: 1.50, locationMultiplier: 0.80, useMultiplier: 1.00, depreciationRate: 5.0, minTax: 2000, maxTax: 150000, notes: 'Preferential rate for SEZ-registered entities per concession agreement.', status: 'active', lastUpdated: '2026-03-20', updatedBy: 'LRA Director', effectiveDate: '2026-01-01' },
  { id: 'VF-009', name: 'Mixed-Use Rental Draft', propertyClass: 'Mixed Use', locationZone: 'Zone A – Central Monrovia', baseRatePerSqm: 2.80, locationMultiplier: 1.60, useMultiplier: 1.30, depreciationRate: 2.0, minTax: 700, maxTax: 40000, notes: 'Draft formula pending LRA Board approval. Not yet applied in Stage 5.', status: 'draft', lastUpdated: '2026-07-01', updatedBy: 'M. Kollie', effectiveDate: '2026-07-01' },
  { id: 'VF-010', name: 'Rural Town Residential (Legacy)', propertyClass: 'Residential', locationZone: 'Zone D – Rural Towns', baseRatePerSqm: 0.45, locationMultiplier: 0.70, useMultiplier: 0.75, depreciationRate: 5.0, minTax: 40, maxTax: 3000, notes: 'Legacy rate — superseded by VF-006 for most cases. Archived.', status: 'archived', lastUpdated: '2025-12-01', updatedBy: 'T. Freeman', effectiveDate: '2025-01-01' },
];

const INITIAL_PARCELS: QueuedParcel[] = [
  { id: 'Q-001', parcelId: 'LRA-MNT-2024-0041', owner: 'Kollie Trading Co.', taxpayerId: 'LRA-TP-10041', county: 'Montserrado', district: 'Central Monrovia', propertyClass: 'Commercial', locationZone: 'Zone A – Central Monrovia', areaSqm: 820, buildingAreaSqm: 740, constructionGrade: 'A', tenureType: 'Freehold', assessedLandValue: 180000, assessedImprovementValue: 320000, totalAssessedValue: 500000, status: 'queued', assignedFormulaId: null, calculatedTax: null, overrideTax: null, overrideReason: '', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-07-01', notes: 'Prime CBD commercial property', flags: [], selected: false },
  { id: 'Q-002', parcelId: 'LRA-MNT-2024-0042', owner: 'Fatima Kamara', taxpayerId: 'LRA-TP-10042', county: 'Montserrado', district: 'Sinkor', propertyClass: 'Residential', locationZone: 'Zone B – Greater Monrovia', areaSqm: 310, buildingAreaSqm: 240, constructionGrade: 'B', tenureType: 'Freehold', assessedLandValue: 28000, assessedImprovementValue: 42000, totalAssessedValue: 70000, status: 'formula_assigned', assignedFormulaId: 'VF-002', calculatedTax: null, overrideTax: null, overrideReason: '', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-07-01', notes: '', flags: [], selected: false },
  { id: 'Q-003', parcelId: 'LRA-MAR-2024-0043', owner: 'Margibi Industrial Corp.', taxpayerId: 'LRA-TP-10043', county: 'Margibi', district: 'Kakata', propertyClass: 'Industrial', locationZone: 'Zone C – Secondary Cities', areaSqm: 4200, buildingAreaSqm: 3100, constructionGrade: 'A', tenureType: 'Leasehold', assessedLandValue: 420000, assessedImprovementValue: 980000, totalAssessedValue: 1400000, status: 'calculated', assignedFormulaId: 'VF-003', calculatedTax: 85000, overrideTax: null, overrideReason: '', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-06-30', notes: 'Large industrial complex — senior review required', flags: ['High-value — senior approval required'], selected: false },
  { id: 'Q-004', parcelId: 'LRA-NIM-2024-0044', owner: 'Nimba Agri Holdings', taxpayerId: 'LRA-TP-10044', county: 'Nimba', district: 'Sanniquellie', propertyClass: 'Farmland', locationZone: 'Zone E – Remote/Rural', areaSqm: 18500, buildingAreaSqm: 0, constructionGrade: 'C', tenureType: 'Customary', assessedLandValue: 22000, assessedImprovementValue: 8000, totalAssessedValue: 30000, status: 'approved', assignedFormulaId: 'VF-004', calculatedTax: 820, overrideTax: null, overrideReason: '', approvedBy: 'J. Jallah', approvedDate: '2026-07-05', forwardedDate: '', s3ApprovedDate: '2026-06-28', notes: 'Customary tenure — verified by Ministry of Agriculture', flags: [], selected: false },
  { id: 'Q-005', parcelId: 'LRA-MNT-2024-0045', owner: 'LPA Holdings Ltd.', taxpayerId: 'LRA-TP-10045', county: 'Montserrado', district: 'Central Monrovia', propertyClass: 'Vacant Land', locationZone: 'Zone A – Central Monrovia', areaSqm: 650, buildingAreaSqm: 0, constructionGrade: 'D', tenureType: 'Freehold', assessedLandValue: 195000, assessedImprovementValue: 0, totalAssessedValue: 195000, status: 'previewed', assignedFormulaId: 'VF-005', calculatedTax: 40000, overrideTax: 28000, overrideReason: 'Partial exemption — pending court dispute on ownership boundary. Reduced by 30% per LRA legal advisory.', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-06-29', notes: 'Court dispute active — override applied', flags: ['Active court dispute', 'Override pending approval'], selected: false },
  { id: 'Q-006', parcelId: 'LRA-BON-2024-0046', owner: 'Bong County Dev. Corp.', taxpayerId: 'LRA-TP-10046', county: 'Bong', district: 'Gbarnga', propertyClass: 'Commercial', locationZone: 'Zone C – Secondary Cities', areaSqm: 1100, buildingAreaSqm: 880, constructionGrade: 'B', tenureType: 'Freehold', assessedLandValue: 88000, assessedImprovementValue: 162000, totalAssessedValue: 250000, status: 'queued', assignedFormulaId: null, calculatedTax: null, overrideTax: null, overrideReason: '', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-07-02', notes: '', flags: [], selected: false },
  { id: 'Q-007', parcelId: 'LRA-LOF-2024-0047', owner: 'Lofa Timber Ltd.', taxpayerId: 'LRA-TP-10047', county: 'Lofa', district: 'Voinjama', propertyClass: 'Farmland', locationZone: 'Zone E – Remote/Rural', areaSqm: 32000, buildingAreaSqm: 0, constructionGrade: 'D', tenureType: 'Concession', assessedLandValue: 38000, assessedImprovementValue: 12000, totalAssessedValue: 50000, status: 'calculated', assignedFormulaId: 'VF-004', calculatedTax: 1200, overrideTax: null, overrideReason: '', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-07-01', notes: 'Concession agreement expires 2030', flags: [], selected: false },
  { id: 'Q-008', parcelId: 'LRA-GBA-2024-0048', owner: 'Grand Bassa Realty', taxpayerId: 'LRA-TP-10048', county: 'Grand Bassa', district: 'Buchanan', propertyClass: 'Residential', locationZone: 'Zone C – Secondary Cities', areaSqm: 480, buildingAreaSqm: 360, constructionGrade: 'A', tenureType: 'Freehold', assessedLandValue: 52000, assessedImprovementValue: 78000, totalAssessedValue: 130000, status: 'queued', assignedFormulaId: null, calculatedTax: null, overrideTax: null, overrideReason: '', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-07-03', notes: '', flags: [], selected: false },
  { id: 'Q-009', parcelId: 'LRA-MNT-2024-0049', owner: 'Monrovia Mixed Dev. Ltd.', taxpayerId: 'LRA-TP-10049', county: 'Montserrado', district: 'Central Monrovia', propertyClass: 'Mixed Use', locationZone: 'Zone B – Greater Monrovia', areaSqm: 1450, buildingAreaSqm: 1200, constructionGrade: 'A', tenureType: 'Freehold', assessedLandValue: 290000, assessedImprovementValue: 560000, totalAssessedValue: 850000, status: 'approved', assignedFormulaId: 'VF-007', calculatedTax: 35000, overrideTax: null, overrideReason: '', approvedBy: 'J. Jallah', approvedDate: '2026-07-04', forwardedDate: '', s3ApprovedDate: '2026-06-27', notes: 'High-value mixed development', flags: [], selected: false },
  { id: 'Q-010', parcelId: 'LRA-MAR-2024-0050', owner: 'Harbel Residential Trust', taxpayerId: 'LRA-TP-10050', county: 'Margibi', district: 'Harbel', propertyClass: 'Residential', locationZone: 'Zone C – Secondary Cities', areaSqm: 220, buildingAreaSqm: 180, constructionGrade: 'B', tenureType: 'Freehold', assessedLandValue: 18000, assessedImprovementValue: 32000, totalAssessedValue: 50000, status: 'forwarded', assignedFormulaId: 'VF-006', calculatedTax: 1440, overrideTax: null, overrideReason: '', approvedBy: 'J. Jallah', approvedDate: '2026-07-02', forwardedDate: '2026-07-03', s3ApprovedDate: '2026-06-25', notes: 'Forwarded to Stage 5 tax assessment', flags: [], selected: false },
  { id: 'Q-011', parcelId: 'LRA-NIM-2024-0051', owner: 'ArcelorMittal Liberia', taxpayerId: 'LRA-TP-10051', county: 'Nimba', district: 'Yekepa', propertyClass: 'Special Economic Zone', locationZone: 'Zone C – Secondary Cities', areaSqm: 45000, buildingAreaSqm: 12000, constructionGrade: 'A', tenureType: 'Concession', assessedLandValue: 3200000, assessedImprovementValue: 4800000, totalAssessedValue: 8000000, status: 'queued', assignedFormulaId: null, calculatedTax: null, overrideTax: null, overrideReason: '', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-07-04', notes: 'SEZ rate applies per concession agreement', flags: ['SEZ rate — verify concession agreement', 'High-value — Director approval required'], selected: false },
  { id: 'Q-012', parcelId: 'LRA-MNT-2024-0052', owner: 'Monrovia Port Authority', taxpayerId: 'LRA-TP-10052', county: 'Montserrado', district: 'Freeport', propertyClass: 'Institutional', locationZone: 'Zone A – Central Monrovia', areaSqm: 12000, buildingAreaSqm: 8000, constructionGrade: 'B', tenureType: 'Government Lease', assessedLandValue: 2400000, assessedImprovementValue: 1800000, totalAssessedValue: 4200000, status: 'calculated', assignedFormulaId: 'VF-001', calculatedTax: 85000, overrideTax: null, overrideReason: '', approvedBy: '', approvedDate: '', forwardedDate: '', s3ApprovedDate: '2026-07-01', notes: 'Government entity — special assessment rules apply', flags: ['Government entity — verify exemption status'], selected: false },
];

const INITIAL_AUDIT: AuditEntry[] = [
  { id: 'A-001', timestamp: '2026-07-08 09:14', action: 'Formula Created', officer: 'J. Jallah', target: 'VF-001', details: 'Central Monrovia Commercial formula created and set to active', oldValue: '—', newValue: 'Active', category: 'formula', status: 'success' },
  { id: 'A-002', timestamp: '2026-07-08 09:45', action: 'Formula Assigned', officer: 'M. Kollie', target: 'Q-002', details: 'VF-002 assigned to LRA-MNT-2024-0042 (Fatima Kamara)', oldValue: 'None', newValue: 'VF-002', category: 'assignment', status: 'info' },
  { id: 'A-003', timestamp: '2026-07-07 10:22', action: 'Batch Calculation', officer: 'J. Jallah', target: 'BATCH', details: 'Batch calculation run on 4 parcels using assigned formulas', oldValue: 'formula_assigned', newValue: 'calculated', category: 'calculation', status: 'success' },
  { id: 'A-004', timestamp: '2026-07-07 11:00', action: 'Override Applied', officer: 'J. Jallah', target: 'Q-005', details: 'Tax override: $40,000 → $28,000. Reason: Court dispute partial exemption', oldValue: '$40,000', newValue: '$28,000', category: 'override', status: 'warning' },
  { id: 'A-005', timestamp: '2026-07-06 14:30', action: 'Assessment Approved', officer: 'J. Jallah', target: 'Q-004', details: 'Farmland assessment approved. Customary tenure verified.', oldValue: 'calculated', newValue: 'approved', category: 'approval', status: 'success' },
  { id: 'A-006', timestamp: '2026-07-05 16:45', action: 'Forwarded to S5', officer: 'J. Jallah', target: 'Q-010', details: 'Parcel LRA-MAR-2024-0050 forwarded to Stage 5 Tax Assessment', oldValue: 'approved', newValue: 'forwarded', category: 'forward', status: 'success' },
  { id: 'A-007', timestamp: '2026-07-05 09:00', action: 'Formula Rate Updated', officer: 'LRA Director', target: 'VF-001', details: 'Commercial Zone A base rate updated: $4.50 → $4.80 per m²', oldValue: '$4.50/m²', newValue: '$4.80/m²', category: 'formula', status: 'info' },
  { id: 'A-008', timestamp: '2026-07-04 10:30', action: 'Assessment Approved', officer: 'J. Jallah', target: 'Q-009', details: 'Mixed use property approved after senior review', oldValue: 'calculated', newValue: 'approved', category: 'approval', status: 'success' },
  { id: 'A-009', timestamp: '2026-07-03 15:20', action: 'Flag Added', officer: 'T. Freeman', target: 'Q-012', details: 'Government entity flag added — exemption status verification required', oldValue: '—', newValue: 'Flag: Government entity', category: 'system', status: 'warning' },
  { id: 'A-010', timestamp: '2026-07-03 08:00', action: 'Formula Archived', officer: 'T. Freeman', target: 'VF-010', details: 'Rural Town Residential (Legacy) archived — superseded by VF-006', oldValue: 'active', newValue: 'archived', category: 'formula', status: 'info' },
];

// ─── Shared Styles ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.07)',
  border: '1px solid var(--dark-border)',
  color: 'var(--dark-text)',
  borderRadius: '6px',
  padding: '6px 10px',
  fontSize: '13px',
  width: '100%',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--dark-muted)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  marginBottom: '4px',
  display: 'block',
};

// ─── Formula Modal ────────────────────────────────────────────────────────────

interface FormulaModalProps {
  formula: ValuationFormula | null;
  onClose: () => void;
  onSave: (f: ValuationFormula) => void;
}

function FormulaModal({ formula, onClose, onSave }: FormulaModalProps) {
  const isNew = !formula?.id || formula.id === '__new__';
  const [form, setForm] = useState({
    name: formula?.name ?? '',
    propertyClass: formula?.propertyClass ?? 'Residential' as PropertyClass,
    locationZone: formula?.locationZone ?? 'Zone A – Central Monrovia' as LocationZone,
    baseRatePerSqm: formula?.baseRatePerSqm ?? 1.00,
    locationMultiplier: formula?.locationMultiplier ?? 1.00,
    useMultiplier: formula?.useMultiplier ?? 1.00,
    depreciationRate: formula?.depreciationRate ?? 0.0,
    minTax: formula?.minTax ?? 100,
    maxTax: formula?.maxTax ?? 50000,
    notes: formula?.notes ?? '',
    status: formula?.status ?? 'draft' as FormulaStatus,
    effectiveDate: formula?.effectiveDate ?? '2026-01-01',
  });
  const [previewArea, setPreviewArea] = useState(500);
  const previewTax = calcTax({ ...form, id: '', lastUpdated: '', updatedBy: '' } as ValuationFormula, previewArea, previewArea * 0.8, 'B');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-10" style={{ borderColor: 'var(--dark-border)', backgroundColor: 'var(--dark-surface)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(212,160,23,0.15)' }}>
              <Calculator size={16} style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <h2 className="font-bold text-sm" style={{ color: 'var(--dark-text)' }}>{isNew ? 'New Valuation Formula' : 'Edit Formula'}</h2>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Stage 4 — Property Valuation</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/5"><X size={16} style={{ color: 'var(--dark-muted)' }} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label style={labelStyle}>Formula Name *</label>
              <input style={inputStyle} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Central Monrovia Commercial" />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value as FormulaStatus }))}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>Property Class</label>
              <select style={inputStyle} value={form.propertyClass} onChange={e => setForm(p => ({ ...p, propertyClass: e.target.value as PropertyClass }))}>
                {PROPERTY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Location Zone</label>
              <select style={inputStyle} value={form.locationZone} onChange={e => setForm(p => ({ ...p, locationZone: e.target.value as LocationZone }))}>
                {LOCATION_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          </div>

          <div className="rounded-lg p-4 space-y-4" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Formula Parameters</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={labelStyle}>Base Rate (USD/m²)</label>
                <input type="number" step="0.01" min="0" style={inputStyle} value={form.baseRatePerSqm} onChange={e => setForm(p => ({ ...p, baseRatePerSqm: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <label style={labelStyle}>Location Multiplier (×)</label>
                <input type="number" step="0.05" min="0" style={inputStyle} value={form.locationMultiplier} onChange={e => setForm(p => ({ ...p, locationMultiplier: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <label style={labelStyle}>Use-Case Multiplier (×)</label>
                <input type="number" step="0.05" min="0" style={inputStyle} value={form.useMultiplier} onChange={e => setForm(p => ({ ...p, useMultiplier: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <label style={labelStyle}>Depreciation Rate (%)</label>
                <input type="number" step="0.5" min="0" max="50" style={inputStyle} value={form.depreciationRate} onChange={e => setForm(p => ({ ...p, depreciationRate: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <label style={labelStyle}>Min Tax (USD)</label>
                <input type="number" step="10" min="0" style={inputStyle} value={form.minTax} onChange={e => setForm(p => ({ ...p, minTax: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <label style={labelStyle}>Max Tax (USD)</label>
                <input type="number" step="100" min="0" style={inputStyle} value={form.maxTax} onChange={e => setForm(p => ({ ...p, maxTax: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
          </div>

          {/* Live Preview */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Live Tax Preview</p>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Area (m²):</span>
                <input type="number" min="1" value={previewArea} onChange={e => setPreviewArea(parseInt(e.target.value) || 100)}
                  style={{ ...inputStyle, width: '80px', padding: '3px 8px' }} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Gross Tax', val: fmt(Math.round(previewArea * form.baseRatePerSqm * form.locationMultiplier * form.useMultiplier)) },
                { label: 'After Depreciation', val: fmt(Math.round(previewArea * form.baseRatePerSqm * form.locationMultiplier * form.useMultiplier * (1 - form.depreciationRate / 100))) },
                { label: 'Estimated Tax', val: fmt(previewTax), highlight: true },
                { label: 'Effective Rate', val: `${((previewTax / (previewArea * form.baseRatePerSqm)) * 100).toFixed(1)}%` },
              ].map(item => (
                <div key={item.label} className="text-center">
                  <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{item.label}</div>
                  <div className="text-sm font-bold" style={{ color: item.highlight ? 'var(--accent)' : 'var(--dark-text)' }}>{item.val}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notes / Justification</label>
            <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Policy basis, LRA circular reference, special conditions..." />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>Cancel</button>
            <button
              onClick={() => {
                if (!form.name.trim()) return;
                onSave({ ...form, id: formula?.id && formula.id !== '__new__' ? formula.id : `VF-${Date.now()}`, lastUpdated: new Date().toISOString().split('T')[0], updatedBy: 'J. Jallah' });
              }}
              className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
              style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}
            >
              <Save size={14} /> {isNew ? 'Create Formula' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Parcel Detail Modal ──────────────────────────────────────────────────────

function ParcelDetailModal({ parcel, formulas, onClose, onAssign, onCalculate, onApprove, onOverride }: {
  parcel: QueuedParcel;
  formulas: ValuationFormula[];
  onClose: () => void;
  onAssign: (parcelId: string, formulaId: string) => void;
  onCalculate: (parcelId: string) => void;
  onApprove: (parcelId: string) => void;
  onOverride: (parcelId: string, amount: number, reason: string) => void;
}) {
  const [selectedFormula, setSelectedFormula] = useState(parcel.assignedFormulaId ?? '');
  const [overrideAmount, setOverrideAmount] = useState(String(parcel.overrideTax ?? parcel.calculatedTax ?? ''));
  const [overrideReason, setOverrideReason] = useState(parcel.overrideReason);
  const [showOverride, setShowOverride] = useState(false);
  const assignedFormula = formulas.find(f => f.id === parcel.assignedFormulaId);
  const qStatus = QUEUE_STATUS_CFG[parcel.status];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-10" style={{ borderColor: 'var(--dark-border)', backgroundColor: 'var(--dark-surface)' }}>
          <div>
            <h2 className="font-bold text-sm" style={{ color: 'var(--dark-text)' }}>{parcel.parcelId}</h2>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{parcel.owner} · {parcel.county}, {parcel.district}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ color: qStatus.color, backgroundColor: qStatus.bg }}>{qStatus.label}</span>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/5"><X size={16} style={{ color: 'var(--dark-muted)' }} /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Property Details */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Property Class', val: parcel.propertyClass },
              { label: 'Location Zone', val: parcel.locationZone.split('–')[0].trim() },
              { label: 'Tenure', val: parcel.tenureType },
              { label: 'Total Area', val: `${parcel.areaSqm.toLocaleString()} m²` },
              { label: 'Building Area', val: `${parcel.buildingAreaSqm.toLocaleString()} m²` },
              { label: 'Construction Grade', val: `Grade ${parcel.constructionGrade}` },
              { label: 'Assessed Land Value', val: fmt(parcel.assessedLandValue) },
              { label: 'Improvement Value', val: fmt(parcel.assessedImprovementValue) },
              { label: 'Total Assessed Value', val: fmt(parcel.totalAssessedValue) },
            ].map(item => (
              <div key={item.label} className="rounded-lg p-3" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{item.label}</div>
                <div className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>{item.val}</div>
              </div>
            ))}
          </div>

          {/* Flags */}
          {parcel.flags.length > 0 && (
            <div className="rounded-lg p-3 flex flex-wrap gap-2" style={{ backgroundColor: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)' }}>
              {parcel.flags.map((f, i) => (
                <span key={i} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}>
                  <AlertTriangle size={10} /> {f}
                </span>
              ))}
            </div>
          )}

          {/* Formula Assignment */}
          <div className="rounded-lg p-4 space-y-3" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Formula Assignment</p>
            <div className="flex gap-3">
              <select style={{ ...inputStyle, flex: 1 }} value={selectedFormula} onChange={e => setSelectedFormula(e.target.value)}>
                <option value="">— Select Formula —</option>
                {formulas.filter(f => f.status === 'active').map(f => (
                  <option key={f.id} value={f.id}>{f.id}: {f.name} ({f.propertyClass})</option>
                ))}
              </select>
              <button
                onClick={() => { if (selectedFormula) onAssign(parcel.id, selectedFormula); }}
                disabled={!selectedFormula}
                className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-40"
                style={{ backgroundColor: 'rgba(96,165,250,0.15)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)' }}
              >
                <Layers size={14} /> Assign
              </button>
            </div>
            {assignedFormula && (
              <div className="rounded-lg p-3 grid grid-cols-4 gap-3" style={{ backgroundColor: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)' }}>
                {[
                  { label: 'Base Rate', val: `$${assignedFormula.baseRatePerSqm}/m²` },
                  { label: 'Location ×', val: `×${assignedFormula.locationMultiplier}` },
                  { label: 'Use ×', val: `×${assignedFormula.useMultiplier}` },
                  { label: 'Depreciation', val: `${assignedFormula.depreciationRate}%` },
                ].map(item => (
                  <div key={item.label} className="text-center">
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{item.label}</div>
                    <div className="text-sm font-semibold" style={{ color: '#60a5fa' }}>{item.val}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Calculated Tax */}
          {parcel.calculatedTax !== null && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#34d399' }}>Calculated Tax</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--dark-text)' }}>{fmt(parcel.overrideTax ?? parcel.calculatedTax)}</p>
                  {parcel.overrideTax !== null && (
                    <p className="text-xs mt-1" style={{ color: '#fbbf24' }}>Override applied (original: {fmt(parcel.calculatedTax)})</p>
                  )}
                </div>
                <button onClick={() => setShowOverride(!showOverride)} className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5" style={{ backgroundColor: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)' }}>
                  <Edit3 size={12} /> Override
                </button>
              </div>
              {showOverride && (
                <div className="mt-3 space-y-2">
                  <input type="number" placeholder="Override amount (USD)" style={inputStyle} value={overrideAmount} onChange={e => setOverrideAmount(e.target.value)} />
                  <textarea rows={2} placeholder="Justification (required)..." style={{ ...inputStyle, resize: 'vertical' }} value={overrideReason} onChange={e => setOverrideReason(e.target.value)} />
                  <button
                    onClick={() => { const amt = parseFloat(overrideAmount); if (!isNaN(amt) && overrideReason.trim().length > 5) { onOverride(parcel.id, amt, overrideReason); setShowOverride(false); } }}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ backgroundColor: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}
                  >
                    Apply Override
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            {parcel.status === 'formula_assigned' && (
              <button onClick={() => onCalculate(parcel.id)} className="flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2" style={{ backgroundColor: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)' }}>
                <Calculator size={14} /> Run Calculation
              </button>
            )}
            {(parcel.status === 'calculated' || parcel.status === 'previewed') && (
              <button onClick={() => onApprove(parcel.id)} className="flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2" style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}>
                <CheckCircle size={14} /> Approve Assessment
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Batch Forward Modal ──────────────────────────────────────────────────────

function BatchForwardModal({ parcels, onClose, onForward }: {
  parcels: QueuedParcel[];
  onClose: () => void;
  onForward: (ids: string[]) => void;
}) {
  const approved = parcels.filter(p => p.status === 'approved');
  const [selected, setSelected] = useState<Set<string>>(new Set(approved.map(p => p.id)));
  const total = Array.from(selected).reduce((sum, id) => {
    const p = approved.find(x => x.id === id);
    return sum + (p?.overrideTax ?? p?.calculatedTax ?? 0);
  }, 0);

  const toggle = (id: string) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => setSelected(selected.size === approved.length ? new Set() : new Set(approved.map(p => p.id)));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-10" style={{ borderColor: 'var(--dark-border)', backgroundColor: 'var(--dark-surface)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(110,231,183,0.15)' }}>
              <Send size={16} style={{ color: '#6ee7b7' }} />
            </div>
            <div>
              <h2 className="font-bold text-sm" style={{ color: 'var(--dark-text)' }}>Batch Forward to Stage 5</h2>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{approved.length} approved valuations ready</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/5"><X size={16} style={{ color: 'var(--dark-muted)' }} /></button>
        </div>

        <div className="p-6 space-y-4">
          {approved.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--dark-muted)' }}>
              <CheckCircle size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No approved valuations to forward.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <button onClick={toggleAll} className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--dark-muted)' }}>
                  {selected.size === approved.length ? <CheckSquare size={14} style={{ color: 'var(--accent)' }} /> : <Square size={14} />}
                  Select All ({approved.length})
                </button>
                <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{selected.size} selected · Total: <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{fmt(total)}</span></div>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {approved.map(p => {
                  const tax = p.overrideTax ?? p.calculatedTax ?? 0;
                  return (
                    <div key={p.id} onClick={() => toggle(p.id)} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors" style={{ backgroundColor: selected.has(p.id) ? 'rgba(110,231,183,0.06)' : 'var(--dark-elevated)', border: `1px solid ${selected.has(p.id) ? 'rgba(110,231,183,0.2)' : 'var(--dark-border)'}` }}>
                      {selected.has(p.id) ? <CheckSquare size={14} style={{ color: '#6ee7b7' }} /> : <Square size={14} style={{ color: 'var(--dark-muted)' }} />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate" style={{ color: 'var(--dark-text)' }}>{p.parcelId}</div>
                        <div className="text-xs truncate" style={{ color: 'var(--dark-muted)' }}>{p.owner} · {p.county}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-bold" style={{ color: '#6ee7b7' }}>{fmt(tax)}</div>
                        <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{p.propertyClass}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(110,231,183,0.06)', border: '1px solid rgba(110,231,183,0.2)' }}>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Parcels to Forward</div>
                    <div className="text-xl font-bold" style={{ color: '#6ee7b7' }}>{selected.size}</div>
                  </div>
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Total Tax Value</div>
                    <div className="text-xl font-bold" style={{ color: 'var(--accent)' }}>{fmtK(total)}</div>
                  </div>
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Destination</div>
                    <div className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>Stage 5 · Tax Assessment</div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>Cancel</button>
                <button
                  onClick={() => { if (selected.size > 0) onForward(Array.from(selected)); }}
                  disabled={selected.size === 0}
                  className="flex-1 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40"
                  style={{ backgroundColor: '#6ee7b7', color: '#070e1f' }}
                >
                  <Send size={14} /> Forward {selected.size} to Stage 5
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ValuationAdminClient() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('formulas');
  const [formulas, setFormulas] = useState<ValuationFormula[]>(INITIAL_FORMULAS);
  const [parcels, setParcels] = useState<QueuedParcel[]>(INITIAL_PARCELS);
  const [audit, setAudit] = useState<AuditEntry[]>(INITIAL_AUDIT);

  // Formula tab state
  const [formulaSearch, setFormulaSearch] = useState('');
  const [formulaStatusFilter, setFormulaStatusFilter] = useState<FormulaStatus | 'all'>('all');
  const [formulaClassFilter, setFormulaClassFilter] = useState<PropertyClass | 'all'>('all');
  const [editingFormula, setEditingFormula] = useState<ValuationFormula | null>(null);
  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const [expandedFormula, setExpandedFormula] = useState<string | null>(null);

  // Queue tab state
  const [queueSearch, setQueueSearch] = useState('');
  const [queueStatusFilter, setQueueStatusFilter] = useState<ParcelQueueStatus | 'all'>('all');
  const [queueClassFilter, setQueueClassFilter] = useState<PropertyClass | 'all'>('all');
  const [selectedParcel, setSelectedParcel] = useState<QueuedParcel | null>(null);
  const [sortField, setSortField] = useState<'parcelId' | 'owner' | 'county' | 'areaSqm' | 'totalAssessedValue'>('parcelId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Preview tab state
  const [previewSearch, setPreviewSearch] = useState('');
  const [previewFormulaFilter, setPreviewFormulaFilter] = useState<string>('all');

  // Batch tab state
  const [showBatchModal, setShowBatchModal] = useState(false);

  // Audit tab state
  const [auditCatFilter, setAuditCatFilter] = useState<AuditEntry['category'] | 'all'>('all');
  const [auditSearch, setAuditSearch] = useState('');

  const addAudit = (entry: Omit<AuditEntry, 'id' | 'timestamp'>) => {
    setAudit(prev => [{
      ...entry,
      id: `A-${Date.now()}`,
      timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
    }, ...prev]);
  };

  // ── Formula Actions ──────────────────────────────────────────────────────────

  const handleSaveFormula = (f: ValuationFormula) => {
    setFormulas(prev => {
      const idx = prev.findIndex(x => x.id === f.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = f; return next; }
      return [f, ...prev];
    });
    addAudit({ action: editingFormula?.id ? 'Formula Updated' : 'Formula Created', officer: 'J. Jallah', target: f.id, details: `${f.name} (${f.propertyClass} · ${f.locationZone})`, oldValue: editingFormula?.id ? 'previous' : '—', newValue: f.status, category: 'formula', status: 'success' });
    setShowFormulaModal(false);
    setEditingFormula(null);
  };

  const handleToggleFormulaStatus = (id: string) => {
    setFormulas(prev => prev.map(f => {
      if (f.id !== id) return f;
      const next = f.status === 'active' ? 'archived' : 'active';
      addAudit({ action: `Formula ${next === 'active' ? 'Activated' : 'Archived'}`, officer: 'J. Jallah', target: id, details: `${f.name} status changed`, oldValue: f.status, newValue: next, category: 'formula', status: 'info' });
      return { ...f, status: next as FormulaStatus };
    }));
  };

  // ── Parcel Actions ───────────────────────────────────────────────────────────

  const handleAssignFormula = (parcelId: string, formulaId: string) => {
    const formula = formulas.find(f => f.id === formulaId);
    setParcels(prev => prev.map(p => p.id === parcelId ? { ...p, assignedFormulaId: formulaId, status: 'formula_assigned' } : p));
    addAudit({ action: 'Formula Assigned', officer: 'J. Jallah', target: parcelId, details: `${formula?.name ?? formulaId} assigned`, oldValue: 'None', newValue: formulaId, category: 'assignment', status: 'info' });
    setSelectedParcel(prev => prev?.id === parcelId ? { ...prev, assignedFormulaId: formulaId, status: 'formula_assigned' } : prev);
  };

  const handleCalculate = (parcelId: string) => {
    setParcels(prev => prev.map(p => {
      if (p.id !== parcelId) return p;
      const formula = formulas.find(f => f.id === p.assignedFormulaId);
      if (!formula) return p;
      const tax = calcTax(formula, p.areaSqm, p.buildingAreaSqm, p.constructionGrade);
      addAudit({ action: 'Tax Calculated', officer: 'J. Jallah', target: parcelId, details: `Formula ${formula.id} applied. Tax: ${fmt(tax)}`, oldValue: 'formula_assigned', newValue: fmt(tax), category: 'calculation', status: 'success' });
      return { ...p, calculatedTax: tax, status: 'calculated' };
    }));
    setSelectedParcel(prev => {
      if (!prev || prev.id !== parcelId) return prev;
      const formula = formulas.find(f => f.id === prev.assignedFormulaId);
      if (!formula) return prev;
      const tax = calcTax(formula, prev.areaSqm, prev.buildingAreaSqm, prev.constructionGrade);
      return { ...prev, calculatedTax: tax, status: 'calculated' };
    });
  };

  const handleBatchCalculate = () => {
    let count = 0;
    setParcels(prev => prev.map(p => {
      if (p.status !== 'formula_assigned' || !p.assignedFormulaId) return p;
      const formula = formulas.find(f => f.id === p.assignedFormulaId);
      if (!formula) return p;
      count++;
      return { ...p, calculatedTax: calcTax(formula, p.areaSqm, p.buildingAreaSqm, p.constructionGrade), status: 'calculated' };
    }));
    if (count > 0) addAudit({ action: 'Batch Calculation', officer: 'J. Jallah', target: 'BATCH', details: `${count} parcels calculated`, oldValue: 'formula_assigned', newValue: 'calculated', category: 'calculation', status: 'success' });
  };

  const handleAutoAssign = () => {
    let count = 0;
    setParcels(prev => prev.map(p => {
      if (p.status !== 'queued') return p;
      const formula = matchFormula(formulas, p.propertyClass, p.locationZone);
      if (!formula) return p;
      count++;
      return { ...p, assignedFormulaId: formula.id, status: 'formula_assigned' };
    }));
    if (count > 0) addAudit({ action: 'Auto-Assign Formulas', officer: 'J. Jallah', target: 'BATCH', details: `${count} parcels auto-assigned formulas by class/zone`, oldValue: 'queued', newValue: 'formula_assigned', category: 'assignment', status: 'success' });
  };

  const handleApprove = (parcelId: string) => {
    setParcels(prev => prev.map(p => p.id === parcelId ? { ...p, status: 'approved', approvedBy: 'J. Jallah', approvedDate: new Date().toISOString().split('T')[0] } : p));
    addAudit({ action: 'Assessment Approved', officer: 'J. Jallah', target: parcelId, details: 'Valuation approved for forwarding to Stage 5', oldValue: 'calculated', newValue: 'approved', category: 'approval', status: 'success' });
    setSelectedParcel(prev => prev?.id === parcelId ? { ...prev, status: 'approved', approvedBy: 'J. Jallah', approvedDate: new Date().toISOString().split('T')[0] } : prev);
  };

  const handleOverride = (parcelId: string, amount: number, reason: string) => {
    setParcels(prev => prev.map(p => p.id === parcelId ? { ...p, overrideTax: amount, overrideReason: reason, status: 'previewed' } : p));
    addAudit({ action: 'Override Applied', officer: 'J. Jallah', target: parcelId, details: `Override: ${fmt(amount)}. Reason: ${reason.slice(0, 60)}`, oldValue: 'calculated', newValue: fmt(amount), category: 'override', status: 'warning' });
    setSelectedParcel(prev => prev?.id === parcelId ? { ...prev, overrideTax: amount, overrideReason: reason, status: 'previewed' } : prev);
  };

  const handleForward = (ids: string[]) => {
    const date = new Date().toISOString().split('T')[0];
    setParcels(prev => prev.map(p => ids.includes(p.id) ? { ...p, status: 'forwarded', forwardedDate: date } : p));
    addAudit({ action: 'Batch Forward to S5', officer: 'J. Jallah', target: 'BATCH', details: `${ids.length} approved valuations forwarded to Stage 5 Tax Assessment`, oldValue: 'approved', newValue: 'forwarded', category: 'forward', status: 'success' });
    setShowBatchModal(false);
  };

  // ── Filtered Data ────────────────────────────────────────────────────────────

  const filteredFormulas = useMemo(() => formulas.filter(f => {
    const matchSearch = !formulaSearch || f.name.toLowerCase().includes(formulaSearch.toLowerCase()) || f.id.toLowerCase().includes(formulaSearch.toLowerCase());
    const matchStatus = formulaStatusFilter === 'all' || f.status === formulaStatusFilter;
    const matchClass = formulaClassFilter === 'all' || f.propertyClass === formulaClassFilter;
    return matchSearch && matchStatus && matchClass;
  }), [formulas, formulaSearch, formulaStatusFilter, formulaClassFilter]);

  const filteredParcels = useMemo(() => {
    let list = parcels.filter(p => {
      const matchSearch = !queueSearch || p.parcelId.toLowerCase().includes(queueSearch.toLowerCase()) || p.owner.toLowerCase().includes(queueSearch.toLowerCase()) || p.county.toLowerCase().includes(queueSearch.toLowerCase());
      const matchStatus = queueStatusFilter === 'all' || p.status === queueStatusFilter;
      const matchClass = queueClassFilter === 'all' || p.propertyClass === queueClassFilter;
      return matchSearch && matchStatus && matchClass;
    });
    list = [...list].sort((a, b) => {
      const va = a[sortField]; const vb = b[sortField];
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return list;
  }, [parcels, queueSearch, queueStatusFilter, queueClassFilter, sortField, sortDir]);

  const calculatedParcels = useMemo(() => parcels.filter(p => ['calculated', 'previewed', 'approved', 'forwarded'].includes(p.status)).filter(p => {
    const matchSearch = !previewSearch || p.parcelId.toLowerCase().includes(previewSearch.toLowerCase()) || p.owner.toLowerCase().includes(previewSearch.toLowerCase());
    const matchFormula = previewFormulaFilter === 'all' || p.assignedFormulaId === previewFormulaFilter;
    return matchSearch && matchFormula;
  }), [parcels, previewSearch, previewFormulaFilter]);

  const filteredAudit = useMemo(() => audit.filter(a => {
    const matchCat = auditCatFilter === 'all' || a.category === auditCatFilter;
    const matchSearch = !auditSearch || a.action.toLowerCase().includes(auditSearch.toLowerCase()) || a.target.toLowerCase().includes(auditSearch.toLowerCase()) || a.officer.toLowerCase().includes(auditSearch.toLowerCase());
    return matchCat && matchSearch;
  }), [audit, auditCatFilter, auditSearch]);

  // ── KPIs ─────────────────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const activeFormulas = formulas.filter(f => f.status === 'active').length;
    const queuedCount = parcels.filter(p => p.status === 'queued').length;
    const calculatedCount = parcels.filter(p => ['calculated', 'previewed'].includes(p.status)).length;
    const approvedCount = parcels.filter(p => p.status === 'approved').length;
    const forwardedCount = parcels.filter(p => p.status === 'forwarded').length;
    const totalTaxValue = parcels.filter(p => p.calculatedTax !== null).reduce((s, p) => s + (p.overrideTax ?? p.calculatedTax ?? 0), 0);
    return { activeFormulas, queuedCount, calculatedCount, approvedCount, forwardedCount, totalTaxValue };
  }, [formulas, parcels]);

  // ── Chart Data ────────────────────────────────────────────────────────────────

  const statusChartData = useMemo(() => {
    const counts: Record<ParcelQueueStatus, number> = { queued: 0, formula_assigned: 0, calculated: 0, previewed: 0, approved: 0, forwarded: 0 };
    parcels.forEach(p => counts[p.status]++);
    return Object.entries(counts).map(([k, v]) => ({ name: QUEUE_STATUS_CFG[k as ParcelQueueStatus].label, value: v, color: QUEUE_STATUS_CFG[k as ParcelQueueStatus].color })).filter(d => d.value > 0);
  }, [parcels]);

  const classChartData = useMemo(() => {
    const map: Record<string, number> = {};
    parcels.filter(p => p.calculatedTax !== null).forEach(p => { map[p.propertyClass] = (map[p.propertyClass] || 0) + (p.overrideTax ?? p.calculatedTax ?? 0); });
    return Object.entries(map).map(([k, v]) => ({ name: k.length > 12 ? k.slice(0, 12) + '…' : k, value: v }));
  }, [parcels]);

  const sortToggle = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => (
    sortField === field
      ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
      : <ChevronDown size={12} style={{ opacity: 0.3 }} />
  );

  // ─── Render ───────────────────────────────────────────────────────────────────

  const tabs: { id: ActiveTab; label: string; icon: React.ReactNode; badge?: string }[] = [
    { id: 'formulas', label: 'Formula Manager', icon: <BookOpen size={14} />, badge: String(formulas.filter(f => f.status === 'active').length) },
    { id: 'queue', label: 'Parcel Queue (S3)', icon: <Layers size={14} />, badge: String(parcels.filter(p => !['forwarded'].includes(p.status)).length) },
    { id: 'preview', label: 'Assessment Preview', icon: <Eye size={14} />, badge: String(calculatedParcels.length) },
    { id: 'batch', label: 'Batch Forward → S5', icon: <Send size={14} />, badge: String(parcels.filter(p => p.status === 'approved').length) },
    { id: 'audit', label: 'Audit Trail', icon: <FileText size={14} /> },
  ];

  return (
    <AppLayout>
      <div className="flex flex-col h-full min-h-screen" style={{ backgroundColor: 'var(--dark-bg)' }}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b" style={{ borderColor: 'var(--dark-border)' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>S4</span>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Stage 4 · Property Valuation</span>
              </div>
              <h1 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--dark-text)' }}>Valuation Admin Dashboard</h1>
              <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>Manage formulas by class/zone, apply to S3 parcels, preview assessments, and forward to Stage 5</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={handleAutoAssign} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium" style={{ backgroundColor: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' }}>
                <Zap size={13} /> Auto-Assign Formulas
              </button>
              <button onClick={handleBatchCalculate} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium" style={{ backgroundColor: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.25)' }}>
                <Calculator size={13} /> Batch Calculate
              </button>
              <button onClick={() => setShowBatchModal(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold" style={{ backgroundColor: 'rgba(110,231,183,0.15)', color: '#6ee7b7', border: '1px solid rgba(110,231,183,0.3)' }}>
                <Send size={13} /> Forward to S5
              </button>
            </div>
          </div>

          {/* KPI Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {[
              { label: 'Active Formulas', val: kpis.activeFormulas, color: 'var(--accent)' },
              { label: 'Queued (S3)', val: kpis.queuedCount, color: '#9ca3af' },
              { label: 'Calculated', val: kpis.calculatedCount, color: '#a78bfa' },
              { label: 'Approved', val: kpis.approvedCount, color: '#34d399' },
              { label: 'Forwarded → S5', val: kpis.forwardedCount, color: '#6ee7b7' },
              { label: 'Total Tax Value', val: fmtK(kpis.totalTaxValue), color: 'var(--accent)', isText: true },
            ].map(kpi => (
              <div key={kpi.label} className="rounded-xl p-3" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
                <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{kpi.label}</div>
                <div className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.val}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto scrollbar-thin pb-1">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all flex-shrink-0 whitespace-nowrap"
                style={{ backgroundColor: activeTab === tab.id ? 'rgba(212,160,23,0.15)' : 'transparent', color: activeTab === tab.id ? 'var(--accent)' : 'var(--dark-muted)', border: `1px solid ${activeTab === tab.id ? 'rgba(212,160,23,0.3)' : 'transparent'}` }}
              >
                {tab.icon} {tab.label}
                {tab.badge && tab.badge !== '0' && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'rgba(212,160,23,0.2)', color: 'var(--accent)' }}>{tab.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── FORMULA MANAGER ─────────────────────────────────────────────── */}
          {activeTab === 'formulas' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                  <input placeholder="Search formulas…" value={formulaSearch} onChange={e => setFormulaSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }} />
                </div>
                <select value={formulaStatusFilter} onChange={e => setFormulaStatusFilter(e.target.value as FormulaStatus | 'all')} className="px-3 py-2 rounded-lg text-xs border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }}>
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
                <select value={formulaClassFilter} onChange={e => setFormulaClassFilter(e.target.value as PropertyClass | 'all')} className="px-3 py-2 rounded-lg text-xs border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }}>
                  <option value="all">All Classes</option>
                  {PROPERTY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button onClick={() => { setEditingFormula({ id: '__new__', name: '', propertyClass: 'Residential', locationZone: 'Zone A – Central Monrovia', baseRatePerSqm: 1, locationMultiplier: 1, useMultiplier: 1, depreciationRate: 0, minTax: 100, maxTax: 50000, notes: '', status: 'draft', lastUpdated: '', updatedBy: '', effectiveDate: '2026-01-01' }); setShowFormulaModal(true); }} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ml-auto" style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}>
                  <Plus size={13} /> New Formula
                </button>
              </div>

              <div className="space-y-2">
                {filteredFormulas.map(f => {
                  const sc = FORMULA_STATUS_CFG[f.status];
                  const isExpanded = expandedFormula === f.id;
                  return (
                    <div key={f.id} className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
                      <div className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-white/[0.02] transition-colors" onClick={() => setExpandedFormula(isExpanded ? null : f.id)}>
                        <div className="w-16 text-xs font-mono font-bold" style={{ color: 'var(--accent)' }}>{f.id}</div>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span style={{ color: 'var(--dark-muted)' }}>{PROP_ICON[f.propertyClass]}</span>
                          <span className="text-sm font-semibold truncate" style={{ color: 'var(--dark-text)' }}>{f.name}</span>
                        </div>
                        <div className="hidden md:flex items-center gap-4 text-xs" style={{ color: 'var(--dark-muted)' }}>
                          <span>{f.propertyClass}</span>
                          <span className="opacity-50">·</span>
                          <span>{f.locationZone.split('–')[0].trim()}</span>
                          <span className="opacity-50">·</span>
                          <span>${f.baseRatePerSqm}/m²</span>
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: sc.color, backgroundColor: sc.bg }}>{sc.label}</span>
                        <div className="flex items-center gap-1 ml-2">
                          <button onClick={e => { e.stopPropagation(); setEditingFormula(f); setShowFormulaModal(true); }} className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title="Edit">
                            <Edit3 size={13} style={{ color: 'var(--dark-muted)' }} />
                          </button>
                          <button onClick={e => { e.stopPropagation(); handleToggleFormulaStatus(f.id); }} className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title={f.status === 'active' ? 'Archive' : 'Activate'}>
                            {f.status === 'active' ? <ToggleRight size={13} style={{ color: '#34d399' }} /> : <ToggleLeft size={13} style={{ color: 'var(--dark-muted)' }} />}
                          </button>
                          {isExpanded ? <ChevronUp size={14} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--dark-muted)' }} />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--dark-border)' }}>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            {[
                              { label: 'Base Rate', val: `$${f.baseRatePerSqm}/m²` },
                              { label: 'Location ×', val: `×${f.locationMultiplier}` },
                              { label: 'Use ×', val: `×${f.useMultiplier}` },
                              { label: 'Depreciation', val: `${f.depreciationRate}%` },
                              { label: 'Min Tax', val: fmt(f.minTax) },
                              { label: 'Max Tax', val: fmt(f.maxTax) },
                              { label: 'Effective Date', val: f.effectiveDate },
                              { label: 'Last Updated', val: `${f.lastUpdated} by ${f.updatedBy}` },
                            ].map(item => (
                              <div key={item.label} className="rounded-lg p-2.5" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                                <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{item.label}</div>
                                <div className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>{item.val}</div>
                              </div>
                            ))}
                          </div>
                          {f.notes && (
                            <div className="mt-3 flex items-start gap-2 text-xs rounded-lg p-3" style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.15)', color: 'var(--dark-muted)' }}>
                              <Info size={12} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                              {f.notes}
                            </div>
                          )}
                          <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.12)' }}>
                            <p className="text-xs font-semibold mb-2" style={{ color: '#60a5fa' }}>Formula: Tax = Area × ${f.baseRatePerSqm} × {f.locationMultiplier} × {f.useMultiplier} × Grade × (1 − {f.depreciationRate}%) → clamped [{fmt(f.minTax)}, {fmt(f.maxTax)}]</p>
                            <div className="flex gap-4 text-xs" style={{ color: 'var(--dark-muted)' }}>
                              <span>500m² Grade B → <strong style={{ color: 'var(--dark-text)' }}>{fmt(calcTax(f, 500, 400, 'B'))}</strong></span>
                              <span>1000m² Grade A → <strong style={{ color: 'var(--dark-text)' }}>{fmt(calcTax(f, 1000, 800, 'A'))}</strong></span>
                              <span>200m² Grade C → <strong style={{ color: 'var(--dark-text)' }}>{fmt(calcTax(f, 200, 160, 'C'))}</strong></span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── PARCEL QUEUE ─────────────────────────────────────────────────── */}
          {activeTab === 'queue' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-48">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                  <input placeholder="Search parcel ID, owner, county…" value={queueSearch} onChange={e => setQueueSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }} />
                </div>
                <select value={queueStatusFilter} onChange={e => setQueueStatusFilter(e.target.value as ParcelQueueStatus | 'all')} className="px-3 py-2 rounded-lg text-xs border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }}>
                  <option value="all">All Statuses</option>
                  {Object.entries(QUEUE_STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={queueClassFilter} onChange={e => setQueueClassFilter(e.target.value as PropertyClass | 'all')} className="px-3 py-2 rounded-lg text-xs border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }}>
                  <option value="all">All Classes</option>
                  {PROPERTY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <span className="text-xs ml-auto" style={{ color: 'var(--dark-muted)' }}>{filteredParcels.length} parcels</span>
              </div>

              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--dark-elevated)' }}>
                      {[
                        { label: 'Parcel ID', field: 'parcelId' as const },
                        { label: 'Owner', field: 'owner' as const },
                        { label: 'County', field: 'county' as const },
                        { label: 'Class', field: null },
                        { label: 'Area (m²)', field: 'areaSqm' as const },
                        { label: 'Assessed Value', field: 'totalAssessedValue' as const },
                        { label: 'Formula', field: null },
                        { label: 'Calc. Tax', field: null },
                        { label: 'Status', field: null },
                        { label: '', field: null },
                      ].map((col, i) => (
                        <th key={i} className="px-3 py-2.5 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>
                          {col.field ? (
                            <button className="flex items-center gap-1 hover:opacity-80" onClick={() => sortToggle(col.field!)}>
                              {col.label} <SortIcon field={col.field} />
                            </button>
                          ) : col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredParcels.map((p, i) => {
                      const sc = QUEUE_STATUS_CFG[p.status];
                      const formula = formulas.find(f => f.id === p.assignedFormulaId);
                      const tax = p.overrideTax ?? p.calculatedTax;
                      return (
                        <tr key={p.id} className="border-t hover:bg-white/[0.02] transition-colors" style={{ borderColor: 'var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                          <td className="px-3 py-2.5 font-mono font-semibold" style={{ color: 'var(--accent)' }}>{p.parcelId}</td>
                          <td className="px-3 py-2.5" style={{ color: 'var(--dark-text)' }}>
                            <div className="truncate max-w-32">{p.owner}</div>
                            <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{p.taxpayerId}</div>
                          </td>
                          <td className="px-3 py-2.5" style={{ color: 'var(--dark-muted)' }}>{p.county}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1" style={{ color: 'var(--dark-text)' }}>
                              {PROP_ICON[p.propertyClass]}
                              <span className="truncate max-w-20">{p.propertyClass}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5" style={{ color: 'var(--dark-text)' }}>{p.areaSqm.toLocaleString()}</td>
                          <td className="px-3 py-2.5 font-semibold" style={{ color: 'var(--dark-text)' }}>{fmtK(p.totalAssessedValue)}</td>
                          <td className="px-3 py-2.5">
                            {formula ? (
                              <span className="text-xs font-mono" style={{ color: '#60a5fa' }}>{formula.id}</span>
                            ) : (
                              <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 font-semibold" style={{ color: tax !== null ? '#34d399' : 'var(--dark-muted)' }}>
                            {tax !== null ? fmt(tax) : '—'}
                            {p.overrideTax !== null && <span className="ml-1 text-xs" style={{ color: '#fbbf24' }}>OVR</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: sc.color, backgroundColor: sc.bg }}>{sc.label}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <button onClick={() => setSelectedParcel(p)} className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title="Review">
                              <Eye size={13} style={{ color: 'var(--dark-muted)' }} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredParcels.length === 0 && (
                  <div className="text-center py-10" style={{ color: 'var(--dark-muted)' }}>
                    <Layers size={28} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No parcels match your filters.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── ASSESSMENT PREVIEW ───────────────────────────────────────────── */}
          {activeTab === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                  <input placeholder="Search parcel or owner…" value={previewSearch} onChange={e => setPreviewSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }} />
                </div>
                <select value={previewFormulaFilter} onChange={e => setPreviewFormulaFilter(e.target.value)} className="px-3 py-2 rounded-lg text-xs border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }}>
                  <option value="all">All Formulas</option>
                  {formulas.filter(f => f.status === 'active').map(f => <option key={f.id} value={f.id}>{f.id}: {f.name}</option>)}
                </select>
                <span className="text-xs ml-auto" style={{ color: 'var(--dark-muted)' }}>{calculatedParcels.length} assessments</span>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--dark-muted)' }}>Pipeline Status</p>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={statusChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={({ name, value }) => `${value}`}>
                        {statusChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Legend iconSize={8} wrapperStyle={{ fontSize: '10px' }} />
                      <Tooltip formatter={(v: number) => [v, 'Parcels']} contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--dark-muted)' }}>Tax Value by Property Class</p>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={classChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} />
                      <YAxis tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} />
                      <Tooltip formatter={(v: number) => [fmt(v), 'Tax']} contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '11px' }} />
                      <Bar dataKey="value" fill="var(--accent)" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Assessment Cards */}
              <div className="space-y-2">
                {calculatedParcels.map(p => {
                  const sc = QUEUE_STATUS_CFG[p.status];
                  const formula = formulas.find(f => f.id === p.assignedFormulaId);
                  const tax = p.overrideTax ?? p.calculatedTax ?? 0;
                  const effectiveRate = p.totalAssessedValue > 0 ? ((tax / p.totalAssessedValue) * 100).toFixed(2) : '0.00';
                  return (
                    <div key={p.id} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-mono font-bold" style={{ color: 'var(--accent)' }}>{p.parcelId}</span>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: sc.color, backgroundColor: sc.bg }}>{sc.label}</span>
                            {p.overrideTax !== null && <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: '#fbbf24', backgroundColor: 'rgba(251,191,36,0.12)' }}>Override</span>}
                          </div>
                          <div className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>{p.owner}</div>
                          <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{p.county}, {p.district} · {p.propertyClass} · {p.areaSqm.toLocaleString()} m²</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold" style={{ color: '#34d399' }}>{fmt(tax)}</div>
                          {p.overrideTax !== null && <div className="text-xs line-through" style={{ color: 'var(--dark-muted)' }}>{fmt(p.calculatedTax ?? 0)}</div>}
                        </div>
                      </div>

                      <div className="grid grid-cols-5 gap-2 text-xs">
                        {[
                          { label: 'Formula', val: formula ? `${formula.id}` : '—', sub: formula?.name ?? '' },
                          { label: 'Base Rate', val: formula ? `$${formula.baseRatePerSqm}/m²` : '—' },
                          { label: 'Assessed Value', val: fmt(p.totalAssessedValue) },
                          { label: 'Effective Rate', val: `${effectiveRate}%` },
                          { label: 'Grade', val: `Grade ${p.constructionGrade}` },
                        ].map(item => (
                          <div key={item.label} className="rounded-lg p-2" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                            <div className="mb-0.5" style={{ color: 'var(--dark-muted)' }}>{item.label}</div>
                            <div className="font-semibold" style={{ color: 'var(--dark-text)' }}>{item.val}</div>
                            {item.sub && <div className="truncate" style={{ color: 'var(--dark-muted)' }}>{item.sub}</div>}
                          </div>
                        ))}
                      </div>

                      {p.flags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {p.flags.map((fl, i) => (
                            <span key={i} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(251,191,36,0.10)', color: '#fbbf24' }}>
                              <AlertTriangle size={9} /> {fl}
                            </span>
                          ))}
                        </div>
                      )}

                      {p.overrideReason && (
                        <div className="mt-2 text-xs rounded-lg p-2" style={{ backgroundColor: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)', color: 'var(--dark-muted)' }}>
                          <strong style={{ color: '#fbbf24' }}>Override reason:</strong> {p.overrideReason}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: 'var(--dark-border)' }}>
                        <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                          {p.approvedBy && <span>Approved by <strong style={{ color: 'var(--dark-text)' }}>{p.approvedBy}</strong> on {p.approvedDate}</span>}
                          {p.forwardedDate && <span className="ml-2" style={{ color: '#6ee7b7' }}>· Forwarded {p.forwardedDate}</span>}
                        </div>
                        <div className="flex gap-2">
                          {(p.status === 'calculated' || p.status === 'previewed') && (
                            <button onClick={() => handleApprove(p.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)' }}>
                              <CheckCircle size={12} /> Approve
                            </button>
                          )}
                          <button onClick={() => setSelectedParcel(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
                            <Eye size={12} /> Detail
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {calculatedParcels.length === 0 && (
                  <div className="text-center py-12" style={{ color: 'var(--dark-muted)' }}>
                    <FlaskConical size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No calculated assessments yet. Assign formulas and run calculations in the Parcel Queue tab.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── BATCH FORWARD ────────────────────────────────────────────────── */}
          {activeTab === 'batch' && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Ready to Forward', val: parcels.filter(p => p.status === 'approved').length, color: '#34d399', desc: 'Approved valuations awaiting S5 dispatch' },
                  { label: 'Already Forwarded', val: parcels.filter(p => p.status === 'forwarded').length, color: '#6ee7b7', desc: 'Sent to Stage 5 Tax Assessment' },
                  { label: 'Pending Approval', val: parcels.filter(p => ['calculated', 'previewed'].includes(p.status)).length, color: '#fbbf24', desc: 'Calculated but not yet approved' },
                ].map(card => (
                  <div key={card.label} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{card.label}</div>
                    <div className="text-3xl font-bold mb-1" style={{ color: card.color }}>{card.val}</div>
                    <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{card.desc}</div>
                  </div>
                ))}
              </div>

              {/* Approved Parcels Table */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
                <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--dark-muted)' }}>Approved Valuations — Ready for Stage 5</p>
                  <button onClick={() => setShowBatchModal(true)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ backgroundColor: '#6ee7b7', color: '#070e1f' }}>
                    <Send size={12} /> Batch Forward to S5
                  </button>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      {['Parcel ID', 'Owner', 'County', 'Class', 'Formula', 'Assessed Value', 'Final Tax', 'Approved By', 'Status'].map(h => (
                        <th key={h} className="px-3 py-2.5 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parcels.filter(p => ['approved', 'forwarded'].includes(p.status)).map((p, i) => {
                      const sc = QUEUE_STATUS_CFG[p.status];
                      const formula = formulas.find(f => f.id === p.assignedFormulaId);
                      const tax = p.overrideTax ?? p.calculatedTax ?? 0;
                      return (
                        <tr key={p.id} className="border-t hover:bg-white/[0.02] transition-colors" style={{ borderColor: 'var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                          <td className="px-3 py-2.5 font-mono font-semibold" style={{ color: 'var(--accent)' }}>{p.parcelId}</td>
                          <td className="px-3 py-2.5 truncate max-w-32" style={{ color: 'var(--dark-text)' }}>{p.owner}</td>
                          <td className="px-3 py-2.5" style={{ color: 'var(--dark-muted)' }}>{p.county}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1" style={{ color: 'var(--dark-text)' }}>
                              {PROP_ICON[p.propertyClass]}
                              <span className="truncate max-w-20">{p.propertyClass}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-mono" style={{ color: '#60a5fa' }}>{formula?.id ?? '—'}</td>
                          <td className="px-3 py-2.5 font-semibold" style={{ color: 'var(--dark-text)' }}>{fmtK(p.totalAssessedValue)}</td>
                          <td className="px-3 py-2.5 font-bold" style={{ color: '#34d399' }}>
                            {fmt(tax)}
                            {p.overrideTax !== null && <span className="ml-1 text-xs" style={{ color: '#fbbf24' }}>OVR</span>}
                          </td>
                          <td className="px-3 py-2.5" style={{ color: 'var(--dark-muted)' }}>{p.approvedBy || '—'}</td>
                          <td className="px-3 py-2.5">
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: sc.color, backgroundColor: sc.bg }}>{sc.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {parcels.filter(p => ['approved', 'forwarded'].includes(p.status)).length === 0 && (
                  <div className="text-center py-10" style={{ color: 'var(--dark-muted)' }}>
                    <Send size={28} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No approved valuations yet. Approve assessments in the Preview tab.</p>
                  </div>
                )}
              </div>

              {/* Forwarded History */}
              {parcels.filter(p => p.status === 'forwarded').length > 0 && (
                <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(110,231,183,0.04)', border: '1px solid rgba(110,231,183,0.15)' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#6ee7b7' }}>Forwarded to Stage 5 — History</p>
                  <div className="space-y-2">
                    {parcels.filter(p => p.status === 'forwarded').map(p => (
                      <div key={p.id} className="flex items-center justify-between text-xs py-2 border-b last:border-0" style={{ borderColor: 'rgba(110,231,183,0.1)' }}>
                        <div className="flex items-center gap-3">
                          <CheckCircle size={12} style={{ color: '#6ee7b7' }} />
                          <span className="font-mono font-semibold" style={{ color: 'var(--accent)' }}>{p.parcelId}</span>
                          <span style={{ color: 'var(--dark-text)' }}>{p.owner}</span>
                          <span style={{ color: 'var(--dark-muted)' }}>{p.county}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-bold" style={{ color: '#6ee7b7' }}>{fmt(p.overrideTax ?? p.calculatedTax ?? 0)}</span>
                          <span style={{ color: 'var(--dark-muted)' }}>Forwarded {p.forwardedDate}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── AUDIT TRAIL ──────────────────────────────────────────────────── */}
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                  <input placeholder="Search action, officer, target…" value={auditSearch} onChange={e => setAuditSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }} />
                </div>
                <select value={auditCatFilter} onChange={e => setAuditCatFilter(e.target.value as AuditEntry['category'] | 'all')} className="px-3 py-2 rounded-lg text-xs border outline-none" style={{ backgroundColor: 'var(--dark-elevated)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }}>
                  <option value="all">All Categories</option>
                  {Object.keys(AUDIT_CAT_CFG).map(k => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
                </select>
                <span className="text-xs ml-auto" style={{ color: 'var(--dark-muted)' }}>{filteredAudit.length} entries</span>
              </div>

              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--dark-elevated)' }}>
                      {['Timestamp', 'Action', 'Officer', 'Target', 'Category', 'Old Value', 'New Value', 'Details'].map(h => (
                        <th key={h} className="px-3 py-2.5 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAudit.map((a, i) => {
                      const cc = AUDIT_CAT_CFG[a.category];
                      const statusColor = a.status === 'success' ? '#34d399' : a.status === 'warning' ? '#fbbf24' : a.status === 'error' ? '#f87171' : '#9ca3af';
                      return (
                        <tr key={a.id} className="border-t hover:bg-white/[0.02] transition-colors" style={{ borderColor: 'var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                          <td className="px-3 py-2.5 font-mono whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>{a.timestamp}</td>
                          <td className="px-3 py-2.5 font-semibold whitespace-nowrap" style={{ color: statusColor }}>{a.action}</td>
                          <td className="px-3 py-2.5" style={{ color: 'var(--dark-text)' }}>{a.officer}</td>
                          <td className="px-3 py-2.5 font-mono" style={{ color: 'var(--accent)' }}>{a.target}</td>
                          <td className="px-3 py-2.5">
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full capitalize" style={{ color: cc.color, backgroundColor: cc.bg }}>{a.category}</span>
                          </td>
                          <td className="px-3 py-2.5 max-w-24 truncate" style={{ color: 'var(--dark-muted)' }}>{a.oldValue}</td>
                          <td className="px-3 py-2.5 max-w-24 truncate font-semibold" style={{ color: 'var(--dark-text)' }}>{a.newValue}</td>
                          <td className="px-3 py-2.5 max-w-48 truncate" style={{ color: 'var(--dark-muted)' }}>{a.details}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredAudit.length === 0 && (
                  <div className="text-center py-10" style={{ color: 'var(--dark-muted)' }}>
                    <FileText size={28} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No audit entries match your filters.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showFormulaModal && (
        <FormulaModal
          formula={editingFormula}
          onClose={() => { setShowFormulaModal(false); setEditingFormula(null); }}
          onSave={handleSaveFormula}
        />
      )}
      {selectedParcel && (
        <ParcelDetailModal
          parcel={selectedParcel}
          formulas={formulas}
          onClose={() => setSelectedParcel(null)}
          onAssign={handleAssignFormula}
          onCalculate={handleCalculate}
          onApprove={handleApprove}
          onOverride={handleOverride}
        />
      )}
      {showBatchModal && (
        <BatchForwardModal
          parcels={parcels}
          onClose={() => setShowBatchModal(false)}
          onForward={handleForward}
        />
      )}
    </AppLayout>
  );
}
